import React, { useState } from 'react';
import { Check, X, Loader2, Wand2, ArrowRight } from 'lucide-react';
import { api } from '../../lib/api';

/* Relecture de la synthèse : chaque champ proposé est comparé à sa valeur
   actuelle et n'est appliqué que si l'administrateur le coche. Rien n'est
   écrasé sans validation — le travail de saisie manuelle est préservé. */

const LABELS = {
  parcours: 'Parcours',
  metier: 'Métier',
  moitie: 'Sa moitié',
  histoire: 'Leur histoire',
  passions: 'Passions',
  valeurs: 'Valeurs',
  ton: 'Ton général',
  expressions: 'Expressions fétiches',
  humour: "Type d'humour",
  traits: 'Traits dominants',
  opinions: 'Opinions tranchées',
  autoderision: 'Autodérision',
  zones_rouges: 'Sujets interdits',
};

const GROUPS = [
  { title: 'Identité', fields: ['parcours', 'metier', 'moitie', 'histoire', 'passions', 'valeurs'] },
  { title: 'Voix & humour', fields: ['ton', 'expressions', 'humour', 'traits', 'opinions', 'autoderision'] },
  {
    title: 'Garde-fous',
    fields: ['zones_rouges'],
    sensitive: true,
    note: "Ce champ pilote le filtre serveur qui bloque les sujets interdits, avant et après le modèle. Relisez-le mot à mot : il est proposé décoché.",
  },
];

export default function SynthesisReview({ result, onApplied, onCancel }) {
  const { proposal, current } = result;
  const changed = Object.keys(proposal).filter((f) => (proposal[f] || '') !== (current[f] || ''));
  const sensitive = new Set(GROUPS.filter((g) => g.sensitive).flatMap((g) => g.fields));
  // Les garde-fous ne sont jamais pré-cochés : ils doivent être validés sciemment.
  const [picked, setPicked] = useState(() => new Set(changed.filter((f) => !sensitive.has(f))));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const toggle = (field) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(field)) next.delete(field); else next.add(field);
      return next;
    });
  };

  const apply = async () => {
    setBusy(true);
    setErr('');
    try {
      const patch = {};
      picked.forEach((f) => { patch[f] = proposal[f]; });
      const persona = await api.patch('/api/admin/persona', patch);
      onApplied(persona, picked.size);
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (changed.length === 0) {
    return (
      <div className="synth-review">
        <div className="test ok"><Check size={15} /> La synthèse ne propose aucun changement : le persona est déjà cohérent avec la matière importée.</div>
        <button className="btn-ghost" onClick={onCancel}>Fermer</button>
      </div>
    );
  }

  return (
    <div className="synth-review">
      <div className="synth-head">
        <span className="synth-count">{changed.length} champ{changed.length > 1 ? 's' : ''} proposé{changed.length > 1 ? 's' : ''}</span>
        <span className="hint">Décochez ce que vous voulez conserver tel quel. Les zones rouges ne sont jamais déduites automatiquement.</span>
      </div>

      {GROUPS.map((group) => {
        const fields = group.fields.filter((f) => changed.includes(f));
        if (fields.length === 0) return null;
        return (
          <div key={group.title} className={`synth-group${group.sensitive ? ' sensitive' : ''}`}>
            <div className="synth-group-title">{group.title}</div>
            {group.note && <div className="synth-note">{group.note}</div>}
            {fields.map((f) => (
              <label key={f} className={`synth-field${picked.has(f) ? ' picked' : ''}`}>
                <input type="checkbox" checked={picked.has(f)} onChange={() => toggle(f)} />
                <div className="synth-body">
                  <div className="synth-label">{LABELS[f] || f}</div>
                  <div className="synth-diff">
                    <div className="synth-before">
                      <span className="synth-tag">actuel</span>
                      {current[f] ? current[f] : <i>vide</i>}
                    </div>
                    <ArrowRight size={14} className="synth-arrow" />
                    <div className="synth-after">
                      <span className="synth-tag new">proposé</span>
                      {proposal[f]}
                    </div>
                  </div>
                </div>
              </label>
            ))}
          </div>
        );
      })}

      {err && <div className="test err"><X size={15} /> {err}</div>}
      <div className="save-bar">
        <button className="btn-primary" onClick={apply} disabled={busy || picked.size === 0}>
          {busy ? <><Loader2 size={15} className="spin" /> Application…</> : <><Wand2 size={15} /> Appliquer {picked.size} champ{picked.size > 1 ? 's' : ''}</>}
        </button>
        <button className="btn-ghost" onClick={onCancel} disabled={busy}>Annuler</button>
      </div>
    </div>
  );
}
